import React, { useState } from 'react';
import { Newspaper, Brain, Calculator, TrendingUp, ArrowLeft } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  category: 'correspondente' | 'ia' | 'leiloes';
  image: string;
  date: string;
  url: string;
}

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'correspondente' | 'ia' | 'leiloes'>('all');

  const blogPosts: BlogPost[] = [
    {
      id: 1,
      title: "Erros comuns ao usar o SICAQ: o que acontece se o proponente estiver errado na proposta?",
      excerpt: "Entenda as consequências de erros no preenchimento do SICAQ e como evitar problemas com a proposta de imóvel da Caixa.",
      category: "correspondente",
      image: "https://images.pexels.com/photos/7621140/pexels-photo-7621140.jpeg",
      date: "21 Mar 2024",
      url: "/blog/erros-comuns-sicaq-proponente-proposta"
    },
    {
      id: 2,
      title: "Leilão, ITBI e Financiamento: 2 perguntas essenciais sobre investimentos",
      excerpt: "Descubra como economizar no ITBI de imóveis arrematados em leilão e as possibilidades de ter múltiplos financiamentos SBPE ativos.",
      category: "leiloes",
      image: "https://images.pexels.com/photos/7621138/pexels-photo-7621138.jpeg",
      date: "20 Mar 2024",
      url: "/blog/leilao-itbi-financiamento-perguntas-essenciais"
    },
    {
      id: 3,
      title: "Como a IA revoluciona a análise de leilões de imóveis",
      excerpt: "Descubra como a inteligência artificial está transformando a maneira de avaliar e escolher imóveis em leilão.",
      category: "ia",
      image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg",
      date: "18 Mar 2024",
      url: "/blog/ia-revoluciona-analise-leiloes"
    },
    {
      id: 4,
      title: "Guia definitivo: Financiamento de imóveis arrematados em leilão",
      excerpt: "Tudo o que você precisa saber sobre como financiar um imóvel comprado em leilão, incluindo documentação e bancos.",
      category: "correspondente",
      image: "https://images.pexels.com/photos/7578934/pexels-photo-7578934.jpeg",
      date: "15 Mar 2024",
      url: "/blog/guia-financiamento-imoveis-leilao"
    },
    {
      id: 5,
      title: "5 estratégias comprovadas para lucrar com leilões de imóveis",
      excerpt: "Aprenda as técnicas mais eficazes para maximizar seus ganhos investindo em imóveis de leilão.",
      category: "leiloes",
      image: "https://images.pexels.com/photos/7621137/pexels-photo-7621137.jpeg",
      date: "12 Mar 2024",
      url: "/blog/estrategias-lucro-leiloes-imoveis"
    },
    {
      id: 6,
      title: "Inteligência Artificial na avaliação de riscos em leilões",
      excerpt: "Como a IA pode identificar e avaliar riscos em editais e matrículas de imóveis em leilão.",
      category: "ia",
      image: "https://images.pexels.com/photos/8386434/pexels-photo-8386434.jpeg",
      date: "10 Mar 2024",
      url: "/blog/ia-avaliacao-riscos-leiloes"
    },
    {
      id: 7,
      title: "Correspondente Bancário: Como aumentar suas vendas em 300%",
      excerpt: "Estratégias comprovadas para correspondentes bancários multiplicarem suas vendas usando tecnologia.",
      category: "correspondente",
      image: "https://images.pexels.com/photos/7578939/pexels-photo-7578939.jpeg",
      date: "8 Mar 2024",
      url: "/blog/correspondente-aumentar-vendas"
    },
    {
      id: 8,
      title: "Machine Learning aplicado ao mercado imobiliário",
      excerpt: "Como algoritmos de aprendizado de máquina estão revolucionando a análise de investimentos imobiliários.",
      category: "ia",
      image: "https://images.pexels.com/photos/8386441/pexels-photo-8386441.jpeg",
      date: "5 Mar 2024",
      url: "/blog/machine-learning-mercado-imobiliario"
    },
    {
      id: 9,
      title: "Análise de risco: Como evitar armadilhas em leilões",
      excerpt: "Guia completo para identificar e evitar os principais riscos ao investir em imóveis de leilão.",
      category: "leiloes",
      image: "https://images.pexels.com/photos/7621139/pexels-photo-7621139.jpeg",
      date: "2 Mar 2024",
      url: "/blog/analise-risco-leiloes"
    }
  ];

  const categories = [
    {
      id: 'all',
      name: 'Todos os Artigos',
      icon: <Newspaper className="h-5 w-5" />
    },
    {
      id: 'correspondente',
      name: 'Correspondente Bancário',
      icon: <Calculator className="h-5 w-5" />
    },
    {
      id: 'ia',
      name: 'Inteligência Artificial',
      icon: <Brain className="h-5 w-5" />
    },
    {
      id: 'leiloes',
      name: 'Leilões e Estratégias',
      icon: <TrendingUp className="h-5 w-5" />
    }
  ];

  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <a 
              href="/"
              className="inline-flex items-center gap-2 text-blue-100 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Voltar ao início
            </a>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-blue-100 font-medium mb-6">
                <Newspaper className="h-5 w-5" />
                <span>Blog ImóvelIA</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Conteúdo Especializado em Leilões
              </h1>
              <p className="text-xl md:text-2xl text-blue-100">
                Artigos, dicas e estratégias para maximizar seus resultados no mercado de leilões imobiliários
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-wrap gap-4 justify-center mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id as typeof activeCategory)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full transition-colors ${
                  activeCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category.icon}
                <span className="font-medium">{category.name}</span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.id}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border border-slate-100"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      post.category === 'correspondente' ? 'bg-green-100 text-green-700' :
                      post.category === 'ia' ? 'bg-purple-100 text-purple-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {post.category === 'correspondente' ? 'Correspondente Bancário' :
                       post.category === 'ia' ? 'Inteligência Artificial' :
                       'Leilões e Estratégias'}
                    </span>
                    <span className="text-sm text-slate-500">{post.date}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-slate-900 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-slate-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <a
                    href={`https://imoveisquegeramrenda.com.br${post.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium group"
                  >
                    Ler artigo completo
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </a>
                </div>
              </article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-600 text-lg">Nenhum artigo encontrado nesta categoria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4 text-slate-900">
              Receba Conteúdo Exclusivo
            </h2>
            <p className="text-slate-600 mb-8">
              Cadastre-se para receber nossos melhores artigos e dicas diretamente no seu email
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Seu melhor e-mail"
                className="flex-1 px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
                Cadastrar
              </button>
            </div>
            <p className="text-sm text-slate-500 mt-4">
              Sem spam. Cancele quando quiser.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;